/**
 * 대화 내 저작 진행 카드.
 *
 * 이 카드가 답하는 질문은 하나다 — **"지금 도는 중인가, 멈춘 건가."** 2026-08-01 인시던트의
 * 실제 비용이 그것이었다(로그를 열어보고서야 8분 전에 끝난 걸 알았다).
 *
 * ⚠ **진행률 막대를 뺐다(UI-D5(a) · P1).** 이전 판은 `pct_est` 를 막대로 그리고 옆에
 * "(추정)"을 붙였는데, 사람이 읽는 것은 라벨이 아니라 막대다 — 85%에서 멈춘 것처럼 보인다는
 * 사인오프 피드백이 그 증거였다. 대신 **관측값만** 쓴다: 실제 경과시간 + 지금 도는 단계 +
 * 대기 순번. 셋 다 서버가 실제로 말한 사실이고, 없으면 표시하지 않는다.
 *
 * 정직 표기 둘은 그대로다:
 *  ① 대기 순번을 숨기지 않는다 — 워커 1개 직렬이라 대기가 정상이고, 숨기면 멈춘 것처럼 보인다.
 *  ② 중단은 되돌릴 수 있는 조작이라 확인 게이트가 없다(절대규칙 5).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check, CircleAlert, Download, FileSpreadsheet, Square, Wrench } from '@/ui/icons'
import { cancelThreadJob, downloadMasterplan, saveBlob } from './api'
import { elapsedSeconds, splitElapsed } from './elapsed'
import { isActive, phaseKey, statusKey } from './types'
import type { JobView } from './types'

/** 경과시간 갱신 주기 — 초 단위 표기라 1초. 진행 중일 때만 돈다. */
const TICK_MS = 1_000

function useElapsedLabel(job: JobView): string | null {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => Date.now())
  const active = isActive(job)

  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [active])

  const seconds = elapsedSeconds(job, now)
  if (seconds === null) return null
  return t(active ? 'job.elapsed' : 'job.elapsed_final', splitElapsed(seconds))
}

export function JobProgressCard({ job, onChanged }: { job: JobView; onChanged: () => void }) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const active = isActive(job)
  const elapsed = useElapsedLabel(job)

  async function onCancel() {
    setBusy(true)
    try {
      const result = await cancelThreadJob(job.thread_id)
      setNotice(result.detail)
      onChanged()
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t('job.cancel_failed'))
    } finally {
      setBusy(false)
    }
  }

  async function onDownload() {
    setBusy(true)
    try {
      const result = await downloadMasterplan(job.thread_id)
      // 3분기 — 아직 파일이 아니면 상태를 말한다(빈 파일을 저장하지 않는다).
      if (result.kind === 'file') saveBlob(result.blob, result.filename)
      else setNotice(String(result.body.detail ?? t('job.not_ready')))
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t('job.download_failed'))
    } finally {
      setBusy(false)
    }
  }

  // 실패는 시스템 오류다 — danger **외곽선** 카드(게이트의 채움과 형태로 구분 · P3′).
  if (job.status === 'failed') {
    return (
      <section className="card error">
        <div className="card-head">
          <CircleAlert size={17} className="err-ic" />
          <span className="card-title">{t('job.failed_title')}</span>
          {elapsed && <span className="card-sub">{elapsed}</span>}
        </div>
        {job.error && <p className="err-sub">{job.error}</p>}
        <p className="err-sub">{t('job.failed_hint')}</p>
        {notice && <p className="card-note">{notice}</p>}
      </section>
    )
  }

  if (job.status === 'cancelled') {
    return (
      <section className="card">
        <div className="card-head">
          <span className="card-title">
            {t('job.title')} · {t(statusKey(job.status))}
          </span>
          {elapsed && <span className="card-sub">{elapsed}</span>}
        </div>
        <div className="card-foot">{t('job.cancelled_hint')}</div>
        {notice && <p className="card-note">{notice}</p>}
      </section>
    )
  }

  if (job.status === 'complete') {
    return (
      <>
        <section className="card">
          <div className="card-head">
            <Check size={16} className="done-ic" />
            <span className="card-title">
              {t('job.title')} · {t(statusKey(job.status))}
            </span>
            {elapsed && <span className="card-sub">{elapsed}</span>}
          </div>
        </section>
        <section className="card file">
          <div className="file-ic">
            <FileSpreadsheet size={19} />
          </div>
          <div className="file-meta">
            {/* 파일명은 서버가 응답 헤더로 준다 — 여기서 지어내지 않는다(내려받아 봐야 안다). */}
            <div className="file-name">{t('job.artifact_title')}</div>
            <div className="file-sub">{t('job.artifact_ready')}</div>
          </div>
          <button type="button" className="btn solid" onClick={() => void onDownload()} disabled={busy}>
            <Download size={14} /> {t('job.download')}
          </button>
          {notice && <p className="card-note">{notice}</p>}
        </section>
      </>
    )
  }

  // queued · running — 관측값만(가짜 % 없음)
  return (
    <section className="card">
      <div className="card-head">
        <span className="spinner" aria-hidden="true" />
        <span className="card-title">
          {t('job.title')} · {t(statusKey(job.status))}
        </span>
        {elapsed && <span className="card-sub">{elapsed}</span>}
      </div>

      <div className="now-tool">
        <Wrench size={13} />
        {t('job.phase_now')} · <code>{t(phaseKey(job.phase))}</code>
        {job.queue_position !== null && (
          <span> · {t('job.queued_ahead', { count: job.queue_position })}</span>
        )}
      </div>

      {/*
        repair 는 **두 번째 8분급 호출**이다. 그 사실을 말해 주지 않으면 사용자에겐 "멈췄다"로
        읽힌다 — 실제로 사인오프에서 그렇게 보였다. 그래서 단계 자체를 설명한다.
      */}
      {job.phase === 'repair' && <p className="card-note">{t('job.repair_note')}</p>}

      <div className="card-foot">{t('job.keep_going')}</div>

      {active && (
        <div className="card-actions">
          <button type="button" className="btn ghost sm" onClick={() => void onCancel()} disabled={busy}>
            <Square size={12} /> {t('job.stop')}
          </button>
        </div>
      )}

      {notice && <p className="card-note">{notice}</p>}
    </section>
  )
}
